import React from 'react';
import PropTypes from 'prop-types';

/*
 volumeAudioProcess and createAudioMeter methods copied and linted from
 https://raw.githubusercontent.com/cwilso/volume-meter/master/volume-meter.js

 Usage:
 audioNode = createAudioMeter(audioContext,clipLevel,averaging,clipLag);

 audioContext: the AudioContext you're using.
 clipLevel: the level (0 to 1) that you would consider "clipping".
 Defaults to 0.98.
 averaging: how "smoothed" you would like the meter to be over time.
 Should be between 0 and less than 1.  Defaults to 0.95.
 clipLag: how long you would like the "clipping" indicator to show
 after clipping has occured, in milliseconds.  Defaults to 750ms.

 Access the clipping through node.checkClipping(); use node.shutdown to get rid of it.
 */
function volumeAudioProcess(event) {
    const buf = event.inputBuffer.getChannelData(0);
    const bufLength = buf.length;
    let sum = 0;
    let x;

    // Do a root-mean-square on the samples: sum up the squares...
    for (let i = 0; i < bufLength; i++) {
        x = buf[i];
        if (Math.abs(x) >= this.clipLevel) {
            this.clipping = true;
            this.lastClip = window.performance.now();
        }
        sum += x * x;
    }

    // ... then take the square root of the sum.
    const rms = Math.sqrt(sum / bufLength);

    // Now smooth this out with the averaging factor applied
    // to the previous sample - take the max here because we
    // want "fast attack, slow release."
    this.volume = Math.max(rms, this.volume * this.averaging);
}

function createAudioMeter(audioContext, clipLevel, averaging, clipLag) {
    const processor = audioContext.createScriptProcessor(512);
    processor.onaudioprocess = volumeAudioProcess;
    processor.clipping = false;
    processor.lastClip = 0;
    processor.volume = 0;
    processor.clipLevel = clipLevel || 0.98;
    processor.averaging = averaging || 0.95;
    processor.clipLag = clipLag || 750;

    // this will have no effect, since we don't copy the input to the output,
    // but works around a current Chrome bug.
    processor.connect(audioContext.destination);

    processor.checkClipping = () => {
        if (!this.clipping) {
            return false;
        }

        if ((this.lastClip + this.clipLag) < window.performance.now()) {
            this.clipping = false;
        }

        return this.clipping;
    };

    processor.shutdown = () => {
        this.disconnect();
        this.onaudioprocess = null;
    };

    return processor;
}

const WIDTH = 500;
const HEIGHT = 50;
let audioContext; // We'll reuse the same audio context, otherwise Safari returns null after 3 instances.

class VolumeMeter extends React.Component {
    static propTypes = {
        stream: PropTypes.object.isRequired,
    };

    componentDidMount() {
        const { stream } = this.props;
        // Create an AudioNode from the stream.
        if (!audioContext) {
            audioContext = new (window.AudioContext || window.webkitAudioContext)();
        }
        const mediaStreamSource = audioContext.createMediaStreamSource(stream);

        // Create a new volume meter and connect it.
        this.meter = createAudioMeter(audioContext);
        mediaStreamSource.connect(this.meter);

        this.canvasContext = this.canvas.getContext('2d');
        this.onLevelChange();
    }

    componentWillUnmount() {
        this.meter.disconnect();
        window.cancelAnimationFrame(this.rafId);
    }

    onLevelChange = () => {
        // clear the background
        this.canvasContext.clearRect(0, 0, WIDTH, HEIGHT);
        this.canvasContext.fillStyle = '#2a6c96';

        // draw a bar based on the current volume
        this.canvasContext.fillRect(0, 0, this.meter.volume * WIDTH * 1.4, HEIGHT);

        // set up the next visual callback
        this.rafId = window.requestAnimationFrame(() => {
            this.onLevelChange();
        });
    };

    render() {
        return (
            <canvas
                ref={(el) => {
                    this.canvas = el;
                }}
                width={WIDTH}
                height={HEIGHT}
            />
        );
    }
}

export default VolumeMeter;
